import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Package } from "react-feather";
import DataTable from "../../../components/common/DataTable";
import StatusBadge from "../../../components/common/StatusBadge";
import DateRangePicker, { useDatePresetRange } from "../../../components/common/DateRangePicker";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useListSurveysQuery } from "./surveysApi";
import { useCanViewAllSurveys } from "./useSurveyPermissions";
import {
  SURVEY_STATUS_CLASS,
  SURVEY_STATUS_LABEL,
  SURVEY_STATUS_TABS,
  formatDate,
  formatQty,
} from "./surveyStatusMaps";
import { paths } from "../../../routes/paths";
import { MAROON } from "../../../components/common/brandColors";
import NewCustomerTag from "./components/NewCustomerTag";

// One big page from the server, paginated client-side by DataTable (which also
// remembers the page across a round trip to the detail) - same approach as the
// fuel lists. The API caps pageSize at 2000.
const PAGE_SIZE = 2000;

const SurveysListPage = () => {
  const navigate = useNavigate();
  const { selectedBranchId } = useBranchFilter();
  const seesAll = useCanViewAllSurveys();
  const { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo } = useDatePresetRange("LAST_30_DAYS");
  const [status, setStatus] = useState("COMPLETED");
  const [mineOnly, setMineOnly] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: page, isLoading, isFetching } = useListSurveysQuery({
    branchId: selectedBranchId ?? undefined,
    status: status || undefined,
    search: search || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    mineOnly: seesAll && mineOnly ? true : undefined,
    pageSize: PAGE_SIZE,
  });
  const rows = page?.items || [];
  const totalCount = page?.totalCount ?? rows.length;

  const columns = useMemo(
    () => [
      {
        // "Customer" - DataTable tints the party column bold blue.
        title: "Customer",
        key: "customer",
        dataIndex: "customerName",
        render: (v, r) => (
          <span>
            {v || r.prospectName || "-"}
            {r.isProspect && <NewCustomerTag />}
            {r.siteLabel && <div className="small text-muted fw-normal">{r.siteLabel}</div>}
          </span>
        ),
      },
      {
        title: "Survey No.",
        key: "surveyNumber",
        dataIndex: "surveyNumber",
        render: (v, r) => (
          <span className="text-muted text-nowrap small">
            {v}
            <div>{r.branchCode}</div>
          </span>
        ),
      },
      { title: "Service", key: "service", dataIndex: "serviceName", render: (v) => v || "-" },
      {
        title: "Created By",
        key: "createdBy",
        dataIndex: "createdByName",
        render: (v, r) => (
          <>
            {v || "-"}
            <div className="small text-muted text-nowrap">{formatDate(r.createdDate)}</div>
          </>
        ),
      },
      {
        title: "Materials",
        key: "materials",
        dataIndex: "lineCount",
        align: "right",
        render: (v, r) => (
          <span className="text-nowrap" title={`${formatQty(r.totalQty)} units in all`}>
            <Package size={13} className="me-1 text-muted" />
            {v || 0}
          </span>
        ),
      },
      {
        title: "Photos",
        key: "photos",
        dataIndex: "photoCount",
        align: "right",
        render: (v, r) => (
          <span className="text-nowrap" title={`${r.attachmentCount || 0} attachments in all`}>
            <Camera size={13} className="me-1 text-muted" />
            {v || 0}
          </span>
        ),
      },
      {
        title: "Status",
        key: "status",
        dataIndex: "status",
        render: (v, r) => (
          <>
            <StatusBadge status={v} classMap={SURVEY_STATUS_CLASS} labelMap={SURVEY_STATUS_LABEL} />
            {v === "QUOTED" && r.quotationNumber && <div className="small fw-semibold mt-1">{r.quotationNumber}</div>}
          </>
        ),
      },
    ],
    []
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Site Surveys</h4>
              <h6>Surveys recorded by technicians in the mobile app - review, edit and turn a completed survey into a quotation</h6>
            </div>
          </div>
        </div>

        {!seesAll && (
          <div className="alert alert-light border small py-2">You are seeing only the surveys you recorded yourself.</div>
        )}

        <div className="d-flex flex-wrap gap-2 mb-3">
          {SURVEY_STATUS_TABS.map((t) => {
            const active = status === t.value;
            return (
              <button
                key={t.value || "ALL"}
                type="button"
                className="btn btn-sm"
                style={{
                  background: active ? MAROON : "#fff",
                  color: active ? "#fff" : MAROON,
                  border: `1px solid ${MAROON}`,
                  fontWeight: 600,
                  minWidth: 90,
                }}
                onClick={() => setStatus(t.value)}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-start">
                <DateRangePicker preset={preset} setPreset={setPreset} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
              </div>
              <div className="table-top flex-wrap gap-2" style={{ rowGap: 8 }}>
                <div className="search-set">
                  <div className="search-input">
                    <input
                      type="text"
                      placeholder="Survey no., customer, contact, service"
                      className="form-control form-control-sm formsearch"
                      style={{ minWidth: 260, height: 34 }}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </div>
                </div>
                {seesAll && (
                  <div className="form-check form-switch mb-0">
                    <input type="checkbox" className="form-check-input" id="surveysMineOnly" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} />
                    <label className="form-check-label" htmlFor="surveysMineOnly">Mine only</label>
                  </div>
                )}
              </div>
            </div>

            {totalCount > rows.length && (
              <div className="alert alert-warning py-2 px-3 mb-2 mt-2">
                Showing the first {rows.length} of {totalCount} matching surveys - narrow the date range or filters for a complete list.
              </div>
            )}

            <div className="table-responsive mt-2">
              <DataTable
                columns={columns}
                dataSource={rows}
                rowKey="recordGuid"
                loading={isLoading || isFetching}
                onRow={(record) => ({
                  onClick: () => navigate(paths.technicalSurveyDetail(record.recordGuid)),
                  style: { cursor: "pointer" },
                })}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveysListPage;
